<template>
	<nav class="portal-nav" aria-label="Portal sections">
		<ul class="portal-nav__list">
			<li v-for="item in items" :key="item.id">
				<RouterLink v-slot="{ href, navigate }" :to="item.path" custom>
					<a
						class="portal-nav__item"
						:class="{ 'portal-nav__item--active': item.id === activeId }"
						:href="href"
						:aria-current="item.id === activeId ? 'page' : undefined"
						@click="onClick($event, navigate)"
					>
						<Text
							tag="span"
							:font="item.id === activeId ? '--text-16-22-semi' : '--text-16-22-med'"
							:color="item.id === activeId ? '--text-accent-brand' : '--text-dark-primary'"
						>
							{{ item.label }}
						</Text>
						<span v-if="item.unread" class="portal-nav__dot" role="img" aria-label="Unread messages" />
					</a>
				</RouterLink>
			</li>
		</ul>
	</nav>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { Text } from '@simple-life-app/growth-ui-kova'
import type { PortalNavItem, PortalSectionId } from '@/portal/sections'

/**
 * The section list (Figma 13813:134680 "Menu Item"): a white card per section, the current one in the
 * accent colour with a bar on its left edge, and the unread dot on Messages. Real links, so a section can
 * be opened in a new tab or bookmarked; the router handles the click and `navigate` tells the host it did —
 * also when the section picked is the one already open, which changes no route (the side menu closes on it).
 */

defineProps<{
	items: PortalNavItem[]
	activeId: PortalSectionId | null
}>()

const emit = defineEmits<{ navigate: [] }>()

function onClick(event: MouseEvent, navigate: (event?: MouseEvent) => Promise<unknown>) {
	void navigate(event)
	// RouterLink prevents the default only for the clicks it routes itself; a modifier click (new tab) stays
	// with the browser and is not a pick.
	if (event.defaultPrevented) emit('navigate')
}
</script>

<style scoped>
.portal-nav__list {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	margin: 0;
	padding: 0;
	list-style: none;
}

/* Figma: 68 tall, 24 at the sides, radius 20. */
.portal-nav__item {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-12);
	min-height: var(--size-68);
	padding: 0 var(--size-24);
	border-radius: var(--size-20);
	overflow: hidden;
	background: var(--background-white);
	text-decoration: none;
}

/* Inset, so the ring survives a scrolling sidebar that clips at the card's edge. */
.portal-nav__item:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: -2px;
}

/* Figma 14361:208650: the 20px accent bar on the card's left edge marks the current section. */
.portal-nav__item--active::before {
	content: '';
	position: absolute;
	top: 50%;
	left: 0;
	width: 3px;
	height: var(--size-20);
	border-radius: 0 2px 2px 0;
	background: var(--text-accent-brand);
	transform: translateY(-50%);
}

.portal-nav__dot {
	flex: none;
	width: var(--size-8);
	height: var(--size-8);
	border-radius: 50%;
	background: var(--button-active-brand);
}
</style>
